import { useMemo } from 'react'

import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { useActiveApp } from 'shared/hooks/useActiveApp'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { useCommonStore } from 'shared/stores/commonStore'
import { getIsInsideWindow } from 'shared/utils/getIsInsideWindow'

import type { AppFocusContext } from './appFocus.types'
import { isActiveWindowForcedFullscreenAtLowWidth } from './appFocusLogic'
import { useDetachedAppLoadStateStore } from './detachedAppLoadState'
import { useWindowStoreHydrated } from './useWindowStoreHydrated'

export const useAppFocusContext = (): AppFocusContext => {
  const isNestedContainer = getIsInsideWindow()
  const isDetachedRoute = useCommonStore(s => s.isDetached)
  const detachedAppLoadState = useDetachedAppLoadStateStore(s => s.state)
  const { isLoading: isAppsLoading } = useAvailableApps()
  const activeApp = useActiveApp()
  const activeAppId = activeApp?.app.id ?? null
  const activeAppType = activeApp?.type ?? null
  const activeWindowId = useWindowStore(s => s.activeWindowId)
  const activeWindowMinimized = useWindowStore(s =>
    activeAppId && activeAppType === 'window'
      ? !!s.windows.find(w => w.id === activeAppId)?.isMinimized
      : false
  )
  const isWindowStoreHydrated = useWindowStoreHydrated()
  const { isTablet, isMobile } = useMediaQuery()

  // WHY: at tablet/mobile width the container renders the active window fullscreen
  // regardless of `isMinimized` in the store — focus tracking must match that render rule.
  const effectiveMinimized =
    activeWindowMinimized &&
    !isActiveWindowForcedFullscreenAtLowWidth({
      isTablet,
      isMobile,
      isActiveWindow: activeAppType === 'window'
    })

  return useMemo(
    () => ({
      isNestedContainer,
      isDetachedRoute,
      detachedAppLoadState,
      isAppsLoading,
      isWindowStoreHydrated,
      activeAppId,
      activeAppType,
      activeWindowId,
      effectiveMinimized
    }),
    [
      activeAppId,
      activeAppType,
      activeWindowId,
      effectiveMinimized,
      detachedAppLoadState,
      isAppsLoading,
      isDetachedRoute,
      isNestedContainer,
      isWindowStoreHydrated
    ]
  )
}
